// Spec 051 Flow F / AC-17 — VAT registration declaration on the FIRST onboarding step.
// The Yes/No answer is mandatory to proceed; the TRN is optional here (approval-gated
// only, per AC-18) and validated as exactly 15 digits when provided.

export interface VatDeclarationValue {
  vatRegistered: boolean | null;
  trn: string;
  vatRegisteredFrom: string;
}

interface VatDeclarationSectionProps {
  value: VatDeclarationValue;
  onChange: (data: Partial<VatDeclarationValue>) => void;
  questionError: string | null;
  trnError: string | null;
}

export const VAT_QUESTION = 'Is your garage registered for VAT?';

// Exact helper texts from Spec 051 (requirements.md Flow F / AC-17) — do not reword.
export const VAT_TRN_HELPER =
  'You can continue registration now — but your VAT details (TRN) are required before your garage can be approved and listed on Garagat.';
export const VAT_NOT_REGISTERED_NOTE =
  "You've declared that your garage is not VAT-registered. Keeping this declaration accurate is your legal responsibility — if your garage is or becomes VAT-registered, you must update this in Settings.";

export default function VatDeclarationSection({
  value,
  onChange,
  questionError,
  trnError,
}: VatDeclarationSectionProps) {
  const handleTrnChange = (raw: string) => {
    // Numeric mask — strip anything that is not a digit, cap at 15.
    onChange({ trn: raw.replace(/[^0-9]/g, '').slice(0, 15) });
  };

  return (
    <fieldset className="vat-declaration" data-testid="vat-declaration">
      <legend className="vat-declaration-title">VAT registration</legend>

      <div
        className="wizard-form-group"
        role="radiogroup"
        aria-label={VAT_QUESTION}
        aria-required="true"
        aria-invalid={questionError ? 'true' : undefined}
      >
        <label>{VAT_QUESTION} *</label>
        <div className="vat-radio-row">
          <label className="vat-radio-option">
            <input
              type="radio"
              name="vat-registered"
              checked={value.vatRegistered === true}
              onChange={() => onChange({ vatRegistered: true })}
            />
            <span>Yes</span>
          </label>
          <label className="vat-radio-option">
            <input
              type="radio"
              name="vat-registered"
              checked={value.vatRegistered === false}
              onChange={() => onChange({ vatRegistered: false })}
            />
            <span>No</span>
          </label>
        </div>
        {questionError && (
          <span className="field-error" role="alert" style={{ color: '#dc2626', fontSize: '0.8125rem', marginTop: '0.25rem', display: 'block' }}>
            {questionError}
          </span>
        )}
      </div>

      {value.vatRegistered === true && (
        <>
          <div className="wizard-form-group">
            <label htmlFor="vat-trn">TRN (Tax Registration Number)</label>
            <input
              id="vat-trn"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={15}
              placeholder="15-digit TRN"
              value={value.trn}
              onChange={(e) => handleTrnChange(e.target.value)}
            />
            {trnError && (
              <span className="field-error" role="alert" style={{ color: '#dc2626', fontSize: '0.8125rem', marginTop: '0.25rem', display: 'block' }}>
                {trnError}
              </span>
            )}
          </div>
          <div className="wizard-form-group">
            <label htmlFor="vat-registered-from">VAT registered from (optional)</label>
            <input
              id="vat-registered-from"
              type="date"
              value={value.vatRegisteredFrom}
              onChange={(e) => onChange({ vatRegisteredFrom: e.target.value })}
            />
          </div>
          <div className="vat-note vat-note-info" role="note">
            {VAT_TRN_HELPER}
          </div>
        </>
      )}

      {value.vatRegistered === false && (
        <div className="vat-note vat-note-warning" role="note">
          {VAT_NOT_REGISTERED_NOTE}
        </div>
      )}
    </fieldset>
  );
}
